import { canAction } from "@utils/accessHelper";
import { AccessLevel } from "@views/configurations/enums/accessLevel";
import { SALE_REFUND_ERROR_MESSAGES } from "@src/enums/SaleRefundErrorEnum";

export const REFUND_PERMISSION = "sales_refund_button";

export const REFUND_SUBJECT_SALE = "venda";
export const REFUND_SUBJECT_CHARGE = "cobrança";

export const REFUND_DISABLED_MESSAGES = {
  PERMISSION: "Sem permissão para reembolsar",
  REFUNDED: "Reembolso já realizado.",
  IN_PROGRESS: "Reembolso já em andamento.",
  GROUP_REFUNDING:
    "Outra cobrança desta assinatura já foi reembolsada ou está em reembolso.",
  STATUS: (subject) => `Reembolso indisponível para o status atual da ${subject}.`,
  PAYMENT: "Disponível após pagamento.",
  DEADLINE: "Prazo de reembolso encerrado.",
  UNAVAILABLE: "Reembolso indisponível.",
  CONTRACT: "Contratos não são reembolsáveis.",
  NOT_LATEST_CHARGE:
    "Só a cobrança mais recente da assinatura pode ser reembolsada.",
  BANK_DETAILS:
    "Reembolso de boleto exige dados bancários. Faça pela tela de Reembolsos.",
  NO_CLAIM: (subject) => `Nenhuma reclamação em aberto para esta ${subject}.`,
};

export const REFUND_STATUS_MESSAGES = {
  refunded: REFUND_DISABLED_MESSAGES.REFUNDED,
  created: REFUND_DISABLED_MESSAGES.PAYMENT,
  waiting_payment: REFUND_DISABLED_MESSAGES.PAYMENT,
};

function resolveMessage(message, subject) {
  return typeof message === "function" ? message(subject) : message;
}

export const REFUND_BLOCKED_REASON_MESSAGES = {
  SALE_NOT_PAID: REFUND_DISABLED_MESSAGES.STATUS,
  REFUND_WINDOW_EXPIRED: REFUND_DISABLED_MESSAGES.DEADLINE,
  NOT_LATEST_PAID_CHARGE: REFUND_DISABLED_MESSAGES.NOT_LATEST_CHARGE,
  GROUP_ALREADY_REFUNDED: REFUND_DISABLED_MESSAGES.GROUP_REFUNDING,
  NO_OPEN_CLAIM: REFUND_DISABLED_MESSAGES.NO_CLAIM,
  REFUND_ALREADY_IN_PROGRESS: REFUND_DISABLED_MESSAGES.IN_PROGRESS,
  METHOD_GATEWAY_NOT_SUPPORTED:
    "Reembolso indisponível para esta forma de pagamento.",
  BANK_DATA_REQUIRED: REFUND_DISABLED_MESSAGES.BANK_DETAILS,
  AUTOMATIC_REFUND_NOT_SUPPORTED:
    "Esta forma de pagamento não é reembolsada automaticamente. Faça pela tela de Reembolsos.",
  SALE_TYPE_NOT_REFUNDABLE: REFUND_DISABLED_MESSAGES.CONTRACT,
  // Codes of the error envelope the refund endpoints answer with (HTTP 409).
  ...SALE_REFUND_ERROR_MESSAGES,
};

export function canRequestRefund(store) {
  return canAction(store, REFUND_PERMISSION, AccessLevel.EDITOR);
}

/**
 * Converts the backend eligibility reason into the copy used by the ERP tooltip.
 * The backend is the source of truth for business eligibility; unknown values stay blocked.
 */
export function getRefundBlockedReasonMessage(reason, subject = REFUND_SUBJECT_SALE) {
  return resolveMessage(
    REFUND_BLOCKED_REASON_MESSAGES[reason] || REFUND_DISABLED_MESSAGES.UNAVAILABLE,
    subject
  );
}

/**
 * Computes the action state from the eligibility contract returned by the sales API.
 * `hasPermission` is intentionally local because `can_refund` does not include screen permission.
 * `status` wins over the blocked reason for the cases in {@link REFUND_STATUS_MESSAGES}.
 */
export function getBackendRefundDisabledReason({
  hasPermission,
  canRefund,
  refundBlockedReason,
  status,
  subject = REFUND_SUBJECT_SALE,
} = {}) {
  if (!hasPermission) return REFUND_DISABLED_MESSAGES.PERMISSION;
  if (canRefund === true) return "";
  if (REFUND_STATUS_MESSAGES[status]) {
    return resolveMessage(REFUND_STATUS_MESSAGES[status], subject);
  }

  return getRefundBlockedReasonMessage(refundBlockedReason, subject);
}

export function getRefundRequestErrorMessage(
  error,
  fallbackMessage = "Não foi possível iniciar o reembolso. Tente novamente.",
  subject = REFUND_SUBJECT_SALE
) {
  const data = getResponseData(error);

  if (
    data.error &&
    Object.prototype.hasOwnProperty.call(REFUND_BLOCKED_REASON_MESSAGES, data.error.code)
  ) {
    return getRefundBlockedReasonMessage(data.error.code, subject);
  }

  if (data.error && typeof data.error.message === "string") {
    return data.error.message;
  }

  if (typeof data.error === "string") return data.error;
  if (typeof data.message === "string") return data.message;

  return fallbackMessage;
}

/**
 * Toast copy for a failed claim refund, or null when the interceptor already toasted it.
 * Legacy 422 answers { sale: [...] }; anything else goes through {@link getRefundRequestErrorMessage}.
 */
export function getClaimRefundErrorToast(
  error,
  fallbackMessage = "Ocorreu um erro ao tentar reembolsar"
) {
  if (error && error.errorNotified) return null;

  const data = getResponseData(error);
  if (Array.isArray(data.sale) && typeof data.sale[0] === "string") {
    return data.sale[0];
  }

  return getRefundRequestErrorMessage(error, fallbackMessage);
}

/**
 * Response body as an object. The legacy axios instance keeps the raw JSON string.
 */
function getResponseData(error) {
  const data = error && error.response && error.response.data;

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  return data && typeof data === "object" ? data : {};
}
